import {
  AlertStatusBadge,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  SeverityDot,
  cn,
  formatRelative,
} from '@minisoc/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { AssistantPanel } from '../components/AssistantPanel';

/**
 * Point d'entrée direct de l'assistant IA.
 *
 * L'assistant est toujours rattaché à une alerte : il n'existe pas de
 * conversation « générale ». Cette page sert donc d'abord à choisir l'alerte sur
 * laquelle lever le doute, en présentant en priorité celles qui restent ouvertes.
 */
export function AssistantPage() {
  const [selected, setSelected] = useState<string | null>(null);

  const alerts = useQuery({
    queryKey: ['alerts', 'assistant'],
    queryFn: () =>
      api.listAlerts({ status: ['OPEN', 'IN_PROGRESS'], pageSize: 25, sort: 'severity' }),
  });

  const rows = alerts.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Assistant IA"
        subtitle="Levée de doute guidée sur une alerte en cours"
      />

      <div className="grid gap-6 xl:grid-cols-[22rem_minmax(0,1fr)]">
        <Card className="self-start">
          <CardHeader title="Alertes en cours" subtitle="Sélectionnez l'alerte à analyser" />

          {rows.length === 0 && !alerts.isPending ? (
            <EmptyState
              title="Aucune alerte en cours"
              description="Rien à analyser pour le moment. Votre parc est sous surveillance continue."
            />
          ) : (
            <ul className="divide-y divide-border-subtle/60">
              {rows.map((alert) => (
                <li key={alert.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(alert.id)}
                    className={cn(
                      'w-full px-5 py-3.5 text-left transition-colors',
                      selected === alert.id
                        ? 'bg-brand-500/8 ring-1 ring-inset ring-brand-500/25'
                        : 'hover:bg-surface-raised',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <SeverityDot severity={alert.severity} />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-text">
                        {alert.ruleNameFr}
                      </span>
                      <AlertStatusBadge status={alert.status} resolution={alert.resolution} />
                    </span>
                    <span className="mt-1 block font-mono text-xs text-text-dim">
                      {alert.endpointName} · {formatRelative(alert.detectedAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {selected ? (
          <AssistantPanel alertId={selected} />
        ) : (
          <Card>
            <EmptyState
              title="Aucune alerte sélectionnée"
              description="Choisissez une alerte à gauche. L'assistant recevra automatiquement son contexte technique et son plan de réaction."
            />
          </Card>
        )}
      </div>
    </>
  );
}
